import { getAuth, signOut } from "firebase/auth";
import { useStore } from "../store";

/**
 * Checks if the current Firebase token is expired or close to expiring
 * @param {number} bufferMinutes - Minutes before expiration to consider "close to expiring" (default: 5)
 * @returns {Promise<boolean>} - True if token is expired or close to expiring
 */
export const isTokenExpiredOrExpiring = async (bufferMinutes = 5) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      return true; // No user means "expired"
    }

    const tokenResult = await user.getIdTokenResult();
    const expirationTime = new Date(tokenResult.expirationTime);
    const now = new Date();
    const bufferTime = bufferMinutes * 60 * 1000; // Convert to milliseconds
    
    // Check if token is expired or will expire within buffer time
    return expirationTime.getTime() - now.getTime() <= bufferTime;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return false; // Treat as transient failure; don't force logout
  }
};

/**
 * Attempts to refresh the Firebase token
 * @returns {Promise<{success: boolean, token?: string, error?: string}>}
 */
export const refreshFirebaseToken = async () => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      return { success: false, error: "No authenticated user" };
    }

    const token = await user.getIdToken(true); // Force refresh
    return { success: true, token };
  } catch (error) {
    console.error("Error refreshing token:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Handles token timeout by properly signing out of Firebase
 */
export const handleTokenTimeout = async () => {
  try {
    const { setLogoutReason } = useStore.getState();
    
    // Set the logout reason first
    setLogoutReason("timeout");
    
    // Properly sign out of Firebase - this will trigger onAuthStateChanged
    // which will clear all the user state properly
    const auth = getAuth();
    await signOut(auth);
  } catch (error) {
    console.error("Error during timeout logout:", error);
    // If signOut fails, fall back to manual state clearing
    const { setUid, setToken, setUser, setCompany } = useStore.getState();
    setUid(null);
    setToken(null);
    setUser(null);
    setCompany(null);
  }
}; 